/**
 * VendorDetail.jsx - Vendor detail page with tabbed interface.
 *
 * Displays comprehensive vendor information organized in tabs:
 *   - Profile: Editable extension fields form
 *   - Catalogue: Items supplied by this vendor with price lines
 *   - Performance: OTIF trend chart, monthly spend, recent lines, price history
 *   - Orders: Purchase orders for this vendor
 *   - Quality: NCRs (non-conformance reports) for this vendor
 *   - Documents: Attachments and registered documents
 *   - ERP Record: Read-only key-value display of BC fields
 *
 * Uses useParams() to get vendor ID from the URL.
 * Data is fetched via getVendorById() from ../api/vendors.
 */

import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  AlertTriangle,
  FileText,
  Plus,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getVendorById, updateVendor } from "../api/vendors";
import { getVendorPerformanceSummary } from "../api/dashboard";
import { getPurchaseOrdersByVendor } from "../api/orders";
import { runReport } from "../api/reports";
import { useCompany } from "../company/CompanyContext";
import { API_BASE } from "../config";
import Tile from "../components/Tile";
import DataTable from "../components/DataTable";
import Badge from "../components/Badge";
import Modal from "../components/Modal";
import Loading from "../components/Loading";
import { useToast } from "../components/Toast";

/* ------------------------------------------------------------------ */
/* Tab definitions                                                     */
/* ------------------------------------------------------------------ */

const TABS = [
  { key: "profile", label: "Profile" },
  { key: "catalogue", label: "Catalogue" },
  { key: "performance", label: "Performance" },
  { key: "orders", label: "Orders" },
  { key: "quality", label: "Quality" },
  { key: "documents", label: "Documents" },
  { key: "erp", label: "ERP Record" },
];

/* ------------------------------------------------------------------ */
/* Column definitions for sub-tables                                   */
/* ------------------------------------------------------------------ */

const CATALOGUE_COLUMNS = [
  { key: "itemNo", label: "Item No." },
  { key: "description", label: "Description" },
  { key: "uom", label: "UOM" },
  { key: "unitCost", label: "Unit Cost", type: "money" },
  { key: "lastOrdered", label: "Last Ordered", type: "date" },
  { key: "leadTime", label: "Lead Time", type: "days" },
];

const ORDERS_COLUMNS = [
  { key: "orderNo", label: "Order No." },
  { key: "orderDate", label: "Order Date", type: "date" },
  { key: "expectedDate", label: "Expected", type: "date" },
  { key: "amount", label: "Amount", type: "money" },
  { key: "lines", label: "Lines", type: "int" },
  {
    key: "status",
    label: "Status",
    render: (val) => <Badge text={val || "Open"} />,
  },
];

const QUALITY_COLUMNS = [
  { key: "ncrNo", label: "NCR No." },
  { key: "date", label: "Date", type: "date" },
  { key: "itemNo", label: "Item No." },
  { key: "description", label: "Description" },
  { key: "quantity", label: "Qty", type: "int" },
  {
    key: "severity",
    label: "Severity",
    render: (val) => {
      const variant = val === "Critical" ? "bad" : val === "Major" ? "bad" : val === "Minor" ? "warn" : "info";
      return <Badge text={val} variant={variant} />;
    },
  },
  {
    key: "status",
    label: "Status",
    render: (val) => <Badge text={val || "Open"} />,
  },
];

const DOCUMENTS_COLUMNS = [
  { key: "name", label: "Document Name" },
  { key: "type", label: "Type" },
  { key: "uploadedBy", label: "Uploaded By" },
  { key: "uploadedAt", label: "Uploaded", type: "date" },
  { key: "size", label: "Size" },
];

const RECENT_LINES_COLUMNS = [
  { key: "itemNo", label: "Item No." },
  { key: "description", label: "Description" },
  { key: "quantity", label: "Qty", type: "int" },
  { key: "unitCost", label: "Unit Cost", type: "money" },
  { key: "receivedDate", label: "Received", type: "date" },
];

const PRICE_HISTORY_COLUMNS = [
  { key: "itemNo", label: "Item No." },
  { key: "description", label: "Description" },
  { key: "oldPrice", label: "Old Price", type: "money" },
  { key: "newPrice", label: "New Price", type: "money" },
  { key: "effectiveDate", label: "Effective", type: "date" },
  {
    key: "change",
    label: "Change%",
    render: (val) => {
      const n = Number(val);
      if (isNaN(n)) return "\u2014";
      const color = n > 0 ? "var(--bad)" : n < 0 ? "var(--good)" : "var(--ink-3)";
      return <span style={{ fontWeight: 600, color }}>{n > 0 ? "+" : ""}{n.toFixed(1)}%</span>;
    },
  },
];

/* ------------------------------------------------------------------ */
/* Profile form field options                                          */
/* ------------------------------------------------------------------ */

const CATEGORY_OPTIONS = [
  "",
  "Raw Materials",
  "Packaging",
  "Services",
  "Equipment",
  "Logistics",
  "MRO Supplies",
  "Chemicals",
  "Electronics",
];

const STATUS_OPTIONS = ["Active", "Inactive", "On Hold", "Blacklisted"];
const RISK_OPTIONS = ["Low", "Medium", "High", "Critical"];
const APPROVAL_OPTIONS = ["Approved", "Pending", "Pending Approval", "Rejected"];
const TIER_OPTIONS = ["Strategic", "Preferred", "Approved", "Probationary", "New"];
const INCOTERM_OPTIONS = ["", "FOB", "CIF", "EXW", "DDP", "DAP", "FCA"];
const PAYMENT_TERMS_OPTIONS = ["", "NET15", "NET30", "NET45", "NET60", "COD", "CIA"];

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function VendorDetail() {
  const { vendorNo } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedCompany } = useCompany();

  /* ---- Core state ---- */
  const [loading, setLoading] = useState(true);
  const [vendor, setVendor] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");

  /* ---- Profile form state ---- */
  const [profileForm, setProfileForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [profileDirty, setProfileDirty] = useState(false);

  /* ---- Tab data state ---- */
  const [catalogueItems, setCatalogueItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [ncrs, setNcrs] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [performanceData, setPerformanceData] = useState(null);
  const [otifTrend, setOtifTrend] = useState([]);
  const [monthlySpend, setMonthlySpend] = useState([]);
  const [recentLines, setRecentLines] = useState([]);
  const [priceHistory, setPriceHistory] = useState([]);

  /* ---- Fetch vendor ---- */
  const fetchVendor = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getVendorById(vendorNo);
      setVendor(data);

      /* Initialize profile form from vendor data (BC API v2.0 fields) */
      setProfileForm({
        category: data.vendorPostingGroup || "",
        subcategory: "",
        region: "",
        businessType: "",
        status: data.blocked && data.blocked !== " " && data.blocked !== "" ? "Blocked" : "Active",
        approval: "Approved",
        riskRating: "Low",
        strategicTier: "Approved",
        tags: "",
        leadTime: "",
        moq: "",
        incoterm: "FOB",
        paymentTerms: data.paymentTermsCode || "NET30",
        primaryContact: data.email || "",
        contactEmail: data.email || "",
        contactPhone: data.phoneNumber || "",
        complianceISO: false,
        complianceSafety: false,
        complianceEnvironmental: false,
        notes: "",
      });

      /* ---- Fetch catalogue items from vendor_products ---- */
      try {
        const token = localStorage.getItem("token") || "";
        const catResp = await fetch(
          `${API_BASE}/api/data/vendors/${encodeURIComponent(vendorNo)}/catalogue`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (catResp.ok) {
          const catData = await catResp.json();
          if (Array.isArray(catData)) {
            setCatalogueItems(catData.map((r, i) => ({
              id: `C${i + 1}`,
              itemNo: r.itemNo || "",
              description: r.description || "",
              uom: r.uom || "",
              unitCost: r.unitCost || 0,
              lastOrdered: r.lastOrdered || "",
              leadTime: r.leadTime || 0,
            })));
          }
        }
      } catch (_) { /* ignore */ }

      /* ---- Fetch NCRs for this vendor ---- */
      try {
        const ncrResp = await fetch(
          `${API_BASE}/api/data/quality/ncrs?vendorNo=${encodeURIComponent(vendorNo)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (ncrResp.ok) {
          const ncrData = await ncrResp.json();
          const rows = ncrData.rows || ncrData || [];
          if (Array.isArray(rows)) {
            setNcrs(rows.map((r, i) => ({
              id: `N${i + 1}`,
              ncrNo: r.ncr_no || r.ncrNo || "",
              date: r.ncr_date || r.date || "",
              itemNo: r.item_no || r.itemNo || "",
              description: r.description || "",
              quantity: r.quantity || 0,
              severity: r.severity || "Minor",
              status: r.status || "Open",
            })));
          }
        }
      } catch (_) { /* ignore */ }

      /* Documents: no upload table yet — leave empty */
      setDocuments([]);

      /* ---- Fetch OTIF trend from otif_analysis report ---- */
      const companyFilter = selectedCompany?.id !== "all" ? selectedCompany?.name : undefined;
      try {
        const otifReport = await runReport("otif_analysis", { days: 180, vendorNo, company: companyFilter });
        if (otifReport && otifReport.rows && otifReport.rows.length > 0) {
          setOtifTrend(otifReport.rows.map((r) => ({
            month: r.month,
            otif: r.otif_pct,
          })));
        }
      } catch (_) { /* ignore */ }

      /* ---- Fetch monthly spend from spend_analysis report ---- */
      try {
        const spendReport = await runReport("spend_analysis", { days: 180, groupBy: "month", company: companyFilter });
        if (spendReport && spendReport.rows && spendReport.rows.length > 0) {
          setMonthlySpend(spendReport.rows.map((r) => ({
            month: r.label || r.month || "",
            spend: r.spend || 0,
          })));
        }
      } catch (_) { /* ignore */ }

      /* ---- Fetch recent PO lines for this vendor ---- */
      try {
        const token2 = localStorage.getItem("token") || "";
        const linesResp = await fetch(
          `${API_BASE}/api/data/vendors/${encodeURIComponent(vendorNo)}/recent-lines`,
          { headers: { Authorization: `Bearer ${token2}` } }
        );
        if (linesResp.ok) {
          const linesData = await linesResp.json();
          if (Array.isArray(linesData)) {
            setRecentLines(linesData.map((r, i) => ({
              id: `RL${i + 1}`,
              itemNo: r.itemNo || "",
              description: r.description || "",
              quantity: r.quantity || 0,
              unitCost: r.unitCost || 0,
              receivedDate: r.receivedDate || "",
            })));
          }
        }
      } catch (_) { /* ignore */ }

      /* ---- Fetch price history from price_variance report ---- */
      try {
        const phReport = await runReport("price_variance", { days: 365, company: companyFilter });
        if (phReport && phReport.rows && phReport.rows.length > 0) {
          setPriceHistory(phReport.rows.slice(0, 10).map((r, i) => ({
            id: `PH${i + 1}`,
            itemNo: r.item_no || "",
            description: r.description || "",
            oldPrice: r.old_cost || 0,
            newPrice: r.new_cost || 0,
            effectiveDate: "",
            change: r.variance_pct || 0,
          })));
        }
      } catch (_) { /* ignore */ }

      /* ---- Fetch purchase orders ---- */
      try {
        const poData = await getPurchaseOrdersByVendor(vendorNo, { top: 50 });
        const mappedOrders = (poData || []).map((po, i) => ({
          id: po.number || `PO-${i}`,
          orderNo: po.number,
          orderDate: po.orderDate || po.documentDate,
          expectedDate: "",
          amount: po.totalAmountIncludingTax || 0,
          lines: po.lineCount || 0,
          status: po.status || "Open",
        }));
        setOrders(mappedOrders);
      } catch {
        setOrders([]);
      }

      /* ---- Fetch vendor performance summary ---- */
      try {
        const perf = await getVendorPerformanceSummary(vendorNo);
        setPerformanceData(perf);
      } catch (_) { /* ignore */ }
    } catch (err) {
      console.error("Vendor detail fetch error:", err);
      const message = err?.response?.data?.error?.message || err.message || "Failed to load vendor details.";
      setError(message);
      toast(message, "bad");
    } finally {
      setLoading(false);
    }
  }, [vendorNo, toast, selectedCompany]);

  useEffect(() => {
    fetchVendor();
  }, [fetchVendor]);

  /* ---- Profile form handlers ---- */
  function handleProfileChange(field, value) {
    setProfileForm((prev) => ({ ...prev, [field]: value }));
    setProfileDirty(true);
  }

  async function handleSaveProfile() {
    setSaving(true);
    try {
      await updateVendor(vendorNo, {
        paymentTermsCode: profileForm.paymentTerms,
        email: profileForm.contactEmail,
        phoneNumber: profileForm.contactPhone,
      });

      toast("Vendor profile updated successfully", "good");
      setProfileDirty(false);
      fetchVendor();
    } catch (err) {
      console.error("Save profile error:", err);
      const message = err?.response?.data?.error?.message || err.message || "Failed to save profile.";
      toast(message, "bad");
    } finally {
      setSaving(false);
    }
  }

  /* ---- Derived display values ---- */
  const vendorName = vendor?.displayName || vendorNo;
  const isBlocked = vendor?.blocked && vendor.blocked !== " " && vendor.blocked !== "";
  const vendorStatus = isBlocked ? "Blocked" : profileForm.status || "Active";
  const approvalStatus = profileForm.approval || "Approved";
  const riskRating = profileForm.riskRating || "Low";

  /* ---- Render ---- */
  if (loading && !vendor) {
    return <Loading show text="Loading vendor details..." />;
  }

  if (error && !vendor) {
    return (
      <div style={styles.errorState}>
        <AlertTriangle size={32} style={{ color: "var(--bad)", marginBottom: 12 }} />
        <h2>Failed to Load Vendor</h2>
        <p className="muted" style={{ marginTop: 8 }}>{error}</p>
        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <button className="btn" onClick={() => navigate("/vendors")}>
            <ArrowLeft size={14} />
            Back to Vendors
          </button>
          <button className="btn primary" onClick={fetchVendor}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Back navigation */}
      <Link to="/vendors" style={styles.backLink}>
        <ArrowLeft size={14} />
        Back to Vendors
      </Link>

      {/* Vendor header */}
      <div style={styles.vendorHeader}>
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {vendorName}
            <span className="muted" style={{ fontSize: 14, fontWeight: 400 }}>
              {vendorNo}
            </span>
          </h1>
          <div style={styles.badgeRow}>
            <Badge text={vendorStatus} />
            <Badge text={approvalStatus} />
            <Badge
              text={riskRating}
              variant={riskRating === "Critical" ? "bad" : riskRating === "High" ? "bad" : riskRating === "Medium" ? "warn" : "good"}
            />
            {isBlocked && <Badge text="Blocked" variant="bad" />}
          </div>
        </div>
      </div>

      {/* KPI Tiles */}
      <div style={styles.tileGrid}>
        <Tile
          label="OTIF%"
          value={performanceData ? `${performanceData.otif}%` : "—"}
          variant={performanceData && performanceData.otif >= 85 ? "good" : performanceData && performanceData.otif >= 70 ? "warn" : "bad"}
          note={`${performanceData?.totalOrders || 0} total orders`}
        />
        <Tile
          label="On Time%"
          value={performanceData ? `${Math.round((performanceData.onTimeOrders / Math.max(performanceData.totalOrders, 1)) * 1000) / 10}%` : "—"}
          variant="good"
          note={`${performanceData?.onTimeOrders || 0} on-time deliveries`}
        />
        <Tile
          label="In Full%"
          value={performanceData ? `${Math.round(((performanceData.totalOrders - performanceData.lateOrders) / Math.max(performanceData.totalOrders, 1)) * 1000) / 10}%` : "—"}
          variant="good"
          note="Quantity accuracy"
        />
        <Tile
          label="Spend"
          value={performanceData ? new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(performanceData.totalSpend || 0) : "—"}
          note={`${performanceData?.transactionCount || 0} transactions`}
        />
        <Tile
          label="Quality Score"
          value={ncrs.length > 0 ? Math.max(0, 100 - ncrs.filter((n) => n.severity === "Critical").length * 20 - ncrs.filter((n) => n.severity === "Major").length * 10) : "—"}
          variant="good"
          note="Out of 100"
        />
        <Tile
          label="Past Due"
          value={orders.filter((o) => o.status === "Overdue").length || 0}
          variant={orders.filter((o) => o.status === "Overdue").length > 0 ? "bad" : "good"}
          note="Open overdue lines"
        />
        <Tile
          label="Lead Time"
          value={`${profileForm.leadTime || "—"}${profileForm.leadTime ? "d" : ""}`}
          note="Average"
        />
        <Tile
          label="Catalogue"
          value={catalogueItems.length}
          note="Items supplied"
        />
      </div>

      {/* Tabs */}
      <div style={styles.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              ...styles.tab,
              ...(activeTab === tab.key ? styles.tabActive : {}),
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={styles.tabContent}>
        {/* ---- Profile Tab ---- */}
        {activeTab === "profile" && (
          <div className="card">
            <div className="card-head">
              <h3>Vendor Profile</h3>
              <button
                className="btn primary"
                onClick={handleSaveProfile}
                disabled={saving || !profileDirty}
              >
                <Save size={14} />
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
            <div className="card-body">
              <div style={styles.formGrid}>
                {/* Category */}
                <div style={styles.formField}>
                  <label style={styles.formLabel}>Category</label>
                  <select
                    value={profileForm.category || ""}
                    onChange={(e) => handleProfileChange("category", e.target.value)}
                    style={styles.formSelect}
                  >
                    {CATEGORY_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt || "Select category..."}</option>
                    ))}
                  </select>
                </div>

                {/* Subcategory */}
                <div style={styles.formField}>
                  <label style={styles.formLabel}>Subcategory</label>
                  <input
                    type="text"
                    value={profileForm.subcategory || ""}
                    onChange={(e) => handleProfileChange("subcategory", e.target.value)}
                    placeholder="e.g. Cardboard, Plastics"
                    style={styles.formInput}
                  />
                </div>

                {/* Region */}
                <div style={styles.formField}>
                  <label style={styles.formLabel}>Region</label>
                  <input
                    type="text"
                    value={profileForm.region || ""}
                    onChange={(e) => handleProfileChange("region", e.target.value)}
                    placeholder="e.g. NCR, Region IV-A"
                    style={styles.formInput}
                  />
                </div>

                {/* Business Type */}
                <div style={styles.formField}>
                  <label style={styles.formLabel}>Business Type</label>
                  <input
                    type="text"
                    value={profileForm.businessType || ""}
                    onChange={(e) => handleProfileChange("businessType", e.target.value)}
                    placeholder="e.g. Manufacturer, Distributor"
                    style={styles.formInput}
                  />
                </div>

                {/* Status */}
                <div style={styles.formField}>
                  <label style={styles.formLabel}>Status</label>
                  <select
                    value={profileForm.status || "Active"}
                    onChange={(e) => handleProfileChange("status", e.target.value)}
                    style={styles.formSelect}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                {/* Approval */}
                <div style={styles.formField}>
                  <label style={styles.formLabel}>Approval Status</label>
                  <select
                    value={profileForm.approval || "Approved"}
                    onChange={(e) => handleProfileChange("approval", e.target.value)}
                    style={styles.formSelect}
                  >
                    {APPROVAL_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                {/* Risk Rating */}
                <div style={styles.formField}>
                  <label style={styles.formLabel}>Risk Rating</label>
                  <select
                    value={profileForm.riskRating || "Low"}
                    onChange={(e) => handleProfileChange("riskRating", e.target.value)}
                    style={styles.formSelect}
                  >
                    {RISK_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                {/* Strategic Tier */}
                <div style={styles.formField}>
                  <label style={styles.formLabel}>Strategic Tier</label>
                  <select
                    value={profileForm.strategicTier || "Approved"}
                    onChange={(e) => handleProfileChange("strategicTier", e.target.value)}
                    style={styles.formSelect}
                  >
                    {TIER_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                {/* Tags */}
                <div style={styles.formField}>
                  <label style={styles.formLabel}>Tags</label>
                  <input
                    type="text"
                    value={profileForm.tags || ""}
                    onChange={(e) => handleProfileChange("tags", e.target.value)}
                    placeholder="Comma-separated tags"
                    style={styles.formInput}
                  />
                </div>

                {/* Lead Time */}
                <div style={styles.formField}>
                  <label style={styles.formLabel}>Lead Time (days)</label>
                  <input
                    type="number"
                    value={profileForm.leadTime || ""}
                    onChange={(e) => handleProfileChange("leadTime", e.target.value)}
                    placeholder="e.g. 7"
                    style={styles.formInput}
                  />
                </div>

                {/* MOQ */}
                <div style={styles.formField}>
                  <label style={styles.formLabel}>Minimum Order Quantity</label>
                  <input
                    type="number"
                    value={profileForm.moq || ""}
                    onChange={(e) => handleProfileChange("moq", e.target.value)}
                    placeholder="e.g. 100"
                    style={styles.formInput}
                  />
                </div>

                {/* Incoterm */}
                <div style={styles.formField}>
                  <label style={styles.formLabel}>Incoterm</label>
                  <select
                    value={profileForm.incoterm || ""}
                    onChange={(e) => handleProfileChange("incoterm", e.target.value)}
                    style={styles.formSelect}
                  >
                    {INCOTERM_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt || "Select incoterm..."}</option>
                    ))}
                  </select>
                </div>

                {/* Payment Terms */}
                <div style={styles.formField}>
                  <label style={styles.formLabel}>Payment Terms</label>
                  <select
                    value={profileForm.paymentTerms || ""}
                    onChange={(e) => handleProfileChange("paymentTerms", e.target.value)}
                    style={styles.formSelect}
                  >
                    {PAYMENT_TERMS_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt || "Select terms..."}</option>
                    ))}
                  </select>
                </div>

                {/* Primary Contact */}
                <div style={styles.formField}>
                  <label style={styles.formLabel}>Primary Contact</label>
                  <input
                    type="text"
                    value={profileForm.primaryContact || ""}
                    onChange={(e) => handleProfileChange("primaryContact", e.target.value)}
                    placeholder="Contact person name"
                    style={styles.formInput}
                  />
                </div>

                {/* Contact Email */}
                <div style={styles.formField}>
                  <label style={styles.formLabel}>Contact Email</label>
                  <input
                    type="email"
                    value={profileForm.contactEmail || ""}
                    onChange={(e) => handleProfileChange("contactEmail", e.target.value)}
                    placeholder="contact@vendor.com"
                    style={styles.formInput}
                  />
                </div>

                {/* Contact Phone */}
                <div style={styles.formField}>
                  <label style={styles.formLabel}>Contact Phone</label>
                  <input
                    type="tel"
                    value={profileForm.contactPhone || ""}
                    onChange={(e) => handleProfileChange("contactPhone", e.target.value)}
                    placeholder="+63 xxx xxx xxxx"
                    style={styles.formInput}
                  />
                </div>
              </div>

              {/* Compliance checkboxes */}
              <div style={styles.complianceSection}>
                <h4 style={{ marginBottom: 10 }}>Compliance</h4>
                <div style={styles.checkboxRow}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={!!profileForm.complianceISO}
                      onChange={(e) => handleProfileChange("complianceISO", e.target.checked)}
                    />
                    ISO Certified
                  </label>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={!!profileForm.complianceSafety}
                      onChange={(e) => handleProfileChange("complianceSafety", e.target.checked)}
                    />
                    Safety Certified
                  </label>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={!!profileForm.complianceEnvironmental}
                      onChange={(e) => handleProfileChange("complianceEnvironmental", e.target.checked)}
                    />
                    Environmental Certified
                  </label>
                </div>
              </div>

              {/* Notes */}
              <div style={{ marginTop: 16 }}>
                <label style={styles.formLabel}>Notes</label>
                <textarea
                  value={profileForm.notes || ""}
                  onChange={(e) => handleProfileChange("notes", e.target.value)}
                  placeholder="Internal notes about this vendor..."
                  rows={4}
                  style={{ ...styles.formInput, resize: "vertical", fontFamily: "inherit" }}
                />
              </div>
            </div>
          </div>
        )}

        {/* ---- Catalogue Tab ---- */}
        {activeTab === "catalogue" && (
          <div className="card">
            <div className="card-head">
              <h3>Items Supplied</h3>
              <span className="muted">{catalogueItems.length} items</span>
            </div>
            <div className="card-body flush">
              <DataTable
                columns={CATALOGUE_COLUMNS}
                rows={catalogueItems}
                emptyText="No items in catalogue"
              />
            </div>
          </div>
        )}

        {/* ---- Performance Tab ---- */}
        {activeTab === "performance" && (
          <div>
            {/* Charts row */}
            <div style={styles.chartRow}>
              <div className="card">
                <div className="card-head">
                  <h3>OTIF Trend</h3>
                  <span className="muted">Last 6 months</span>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={otifTrend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--line-2)" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--ink-3)" }} tickLine={false} />
                      <YAxis domain={[50, 100]} tick={{ fontSize: 11, fill: "var(--ink-3)" }} tickLine={false} tickFormatter={(v) => `${v}%`} />
                      <Tooltip
                        contentStyle={{ background: "var(--panel)", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", fontSize: 12 }}
                        formatter={(value) => [`${value}%`, "OTIF"]}
                      />
                      <Line type="monotone" dataKey="otif" stroke="var(--accent)" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="card">
                <div className="card-head">
                  <h3>Monthly Spend</h3>
                  <span className="muted">Last 6 months</span>
                </div>
                <div className="card-body">
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={monthlySpend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--line-2)" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--ink-3)" }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: "var(--ink-3)" }} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                      <Tooltip
                        contentStyle={{ background: "var(--panel)", border: "1px solid var(--line)", borderRadius: "var(--radius-sm)", fontSize: 12 }}
                        formatter={(value) => [
                          new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP", minimumFractionDigits: 0 }).format(value),
                          "Spend",
                        ]}
                      />
                      <Bar dataKey="spend" fill="var(--teal)" radius={[4, 4, 0, 0]} maxBarSize={40} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Recent Lines */}
            <div className="card">
              <div className="card-head">
                <h3>Recent Receipt Lines</h3>
              </div>
              <div className="card-body flush">
                <DataTable
                  columns={RECENT_LINES_COLUMNS}
                  rows={recentLines}
                  emptyText="No recent receipts"
                />
              </div>
            </div>

            {/* Price History */}
            <div className="card">
              <div className="card-head">
                <h3>Price History</h3>
              </div>
              <div className="card-body flush">
                <DataTable
                  columns={PRICE_HISTORY_COLUMNS}
                  rows={priceHistory}
                  emptyText="No price changes recorded"
                />
              </div>
            </div>
          </div>
        )}

        {/* ---- Orders Tab ---- */}
        {activeTab === "orders" && (
          <div className="card">
            <div className="card-head">
              <h3>Purchase Orders</h3>
              <span className="muted">{orders.length} orders</span>
            </div>
            <div className="card-body flush">
              <DataTable
                columns={ORDERS_COLUMNS}
                rows={orders}
                onRow={(row) => navigate(`/orders/${row.orderNo}`)}
                emptyText="No purchase orders found"
              />
            </div>
          </div>
        )}

        {/* ---- Quality Tab ---- */}
        {activeTab === "quality" && (
          <div className="card">
            <div className="card-head">
              <h3>Non-Conformance Reports (NCRs)</h3>
              <button className="btn primary tiny">
                <Plus size={12} />
                Raise NCR
              </button>
            </div>
            <div className="card-body flush">
              <DataTable
                columns={QUALITY_COLUMNS}
                rows={ncrs}
                emptyText="No NCRs recorded for this vendor"
              />
            </div>
          </div>
        )}

        {/* ---- Documents Tab ---- */}
        {activeTab === "documents" && (
          <div className="card">
            <div className="card-head">
              <h3>Documents & Attachments</h3>
              <button className="btn primary tiny">
                <FileText size={12} />
                Register Document
              </button>
            </div>
            <div className="card-body flush">
              <DataTable
                columns={DOCUMENTS_COLUMNS}
                rows={documents}
                emptyText="No documents registered"
              />
            </div>
          </div>
        )}

        {/* ---- ERP Record Tab ---- */}
        {activeTab === "erp" && vendor && (
          <div className="card">
            <div className="card-head">
              <h3>Business Central Record</h3>
              <span className="muted">Read-only</span>
            </div>
            <div className="card-body">
              <div style={styles.erpGrid}>
                {Object.entries(vendor)
                  .filter(([key]) => !key.startsWith("odata") && !key.startsWith("ETag"))
                  .map(([key, value]) => (
                    <div key={key} style={styles.erpRow}>
                      <dt style={styles.erpKey}>{key.replace(/_/g, " ")}</dt>
                      <dd style={styles.erpValue}>
                        {value != null && value !== "" ? String(value) : "\u2014"}
                      </dd>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const styles = {
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
    fontWeight: 550,
    color: "var(--ink-3)",
    textDecoration: "none",
    marginBottom: 16,
  },
  vendorHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    marginBottom: 20,
    flexWrap: "wrap",
  },
  badgeRow: {
    display: "flex",
    gap: 6,
    marginTop: 8,
    flexWrap: "wrap",
  },
  tileGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
    gap: 12,
    marginBottom: 20,
  },
  tabBar: {
    display: "flex",
    gap: 0,
    borderBottom: "1px solid var(--line)",
    marginBottom: 16,
    overflowX: "auto",
  },
  tab: {
    padding: "10px 18px",
    fontSize: 13,
    fontWeight: 550,
    color: "var(--ink-3)",
    background: "none",
    border: "none",
    borderBottom: "2px solid transparent",
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "color 0.12s, border-color 0.12s",
  },
  tabActive: {
    color: "var(--accent)",
    borderBottomColor: "var(--accent)",
    fontWeight: 650,
  },
  tabContent: {
    minHeight: 300,
  },
  chartRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))",
    gap: 16,
    marginBottom: 16,
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 14,
  },
  formField: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  formLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "var(--ink-2)",
  },
  formInput: {
    font: "inherit",
    fontSize: 13,
    padding: "8px 10px",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-sm)",
    background: "var(--panel)",
    color: "var(--ink)",
    outline: "none",
  },
  formSelect: {
    font: "inherit",
    fontSize: 13,
    padding: "8px 10px",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-sm)",
    background: "var(--panel)",
    color: "var(--ink)",
    outline: "none",
    cursor: "pointer",
  },
  complianceSection: {
    marginTop: 20,
    paddingTop: 16,
    borderTop: "1px solid var(--line-2)",
  },
  checkboxRow: {
    display: "flex",
    gap: 24,
    flexWrap: "wrap",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
    fontWeight: 550,
    color: "var(--ink-2)",
    cursor: "pointer",
  },
  erpGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 0,
  },
  erpRow: {
    display: "flex",
    gap: 16,
    padding: "8px 0",
    borderBottom: "1px solid var(--line-2)",
  },
  erpKey: {
    flex: "0 0 240px",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--ink-3)",
    textTransform: "capitalize",
    margin: 0,
  },
  erpValue: {
    flex: 1,
    fontSize: 13,
    color: "var(--ink)",
    fontFamily: "monospace",
    wordBreak: "break-all",
    margin: 0,
  },
  errorState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 400,
    textAlign: "center",
  },
};
